import { APP_CONFIG } from "config/appConfig";
import Keycloak from "keycloak-js";

class KeycloakService {
    private static instance: KeycloakService;
    private keycloak: Keycloak | null = null;
    private initialized: boolean = false;
    private initPromise: Promise<boolean> | null = null;
    private readonly initTimeoutMs: number = 8000;

    private constructor() { }

    public static getInstance(): KeycloakService {
        if (!KeycloakService.instance) {
            KeycloakService.instance = new KeycloakService();
        }
        return KeycloakService.instance;
    }

    public async init(): Promise<boolean> {
        // If already initialized, return the current authentication state
        if (this.initialized && this.keycloak) {
            return this.keycloak.authenticated || false;
        }

        // If initialization is in progress, wait for it
        if (this.initPromise) {
            return this.initPromise;
        }

        // Start initialization and store the promise
        this.initPromise = this.performInit();

        try {
            const result = await this.initPromise;

            return result;
        } finally {
            // Clear the promise after completion (success or failure)
            this.initPromise = null;
        }
    }

    private async performInit(): Promise<boolean> {
        try {
            this.keycloak = new Keycloak({
                url: APP_CONFIG.APP_KEYCLOAK_BASE_URL,
                realm: APP_CONFIG.APP_KEYCLOAK_REALM,
                clientId: APP_CONFIG.APP_KEYCLOAK_CLIENT_ID,
            });

            // Initialize Keycloak with increased timeSkew to handle server time differences
            // timeSkew allows tokens to be valid even with time synchronization issues
            const timeoutFallback = "keycloak-init-timeout";
            const authenticated = await Promise.race([
                this.keycloak.init({
                    onLoad: "check-sso",
                    pkceMethod: "S256",
                    checkLoginIframe: false,

                    // By right check-sso should need this RedirectUri but its not working as expected. I doubt its because of the version of keycloak.js
                    silentCheckSsoRedirectUri: `${globalThis.location.origin}${APP_CONFIG.APP_PUBLIC_URL}/silent-check-sso.html`,
                    silentCheckSsoFallback: false, // Allow fallback to regular check-sso if needed
                    timeSkew: 60,
                }),
                new Promise<typeof timeoutFallback>((resolve) => {
                    globalThis.setTimeout(
                        () => resolve(timeoutFallback),
                        this.initTimeoutMs
                    );
                }),
            ]);

            if (authenticated === timeoutFallback) {
                console.error(
                    `Keycloak initialization timed out after ${this.initTimeoutMs}ms`
                );
                this.initialized = false;
                this.keycloak = null;
                return false;
            }

            this.initialized = true;

            return authenticated;
        } catch (error) {
            console.error("Keycloak initialization failed:", error);
            this.initialized = false;
            this.keycloak = null;
            return false;
        }
    }

    public async login(): Promise<void> {
        if (!this.keycloak) {
            console.error("Keycloak not initialized");
            return;
        }

        try {
            await this.keycloak.login({
                redirectUri: `${globalThis.location.origin}${APP_CONFIG.APP_PUBLIC_URL}/`,
            });
        } catch (error) {
            console.error("Login failed:", error);
            throw error;
        }
    }

    public async register(): Promise<void> {
        if (!this.keycloak) {
            console.error("Keycloak not initialized");
            return;
        }

        this.keycloak.register({
            // Return to app root so post-auth routing is decided by app logic
            // (existing user -> dashboard, missing account -> sign-up onboarding).
            redirectUri: `${globalThis.location.origin}${APP_CONFIG.APP_PUBLIC_URL}/`,
        });
    }

    public logout(): void {
        if (!this.keycloak) {
            throw new Error("Keycloak not initialized");
        }

        const idToken = this.keycloak.idToken;

        // Build logout URL
        const logoutUrl = new URL(
            `${APP_CONFIG.APP_KEYCLOAK_BASE_URL}/realms/${APP_CONFIG.APP_KEYCLOAK_REALM}/protocol/openid-connect/logout`
        );

        // Prepare request body as URL-encoded form data
        logoutUrl.searchParams.append(
            "client_id",
            APP_CONFIG.APP_KEYCLOAK_CLIENT_ID
        );
        if (idToken) {
            // It only exist when user login by OpenID, otherwise it will be undefined
            logoutUrl.searchParams.append("id_token_hint", idToken);
        }
        logoutUrl.searchParams.append(
            "post_logout_redirect_uri",
            `${globalThis.location.origin}${APP_CONFIG.APP_PUBLIC_URL}/`
        );

        // Clear local app state BEFORE redirect
        localStorage.removeItem("keycloak-token");
        localStorage.removeItem("keycloak-refresh-token");
        sessionStorage.clear();

        // Redirect to logout URL
        globalThis.location.href = logoutUrl.toString();
    }

    // Force refresh token regardless of validity
    async forceRefreshToken(): Promise<string | undefined> {
        try {
            await this.ensureInitialized();

            // Force refresh by setting minValidity very high
            await this.keycloak?.updateToken(9999);

            const newToken = this.keycloak?.token;

            return newToken;
        } catch (error) {
            console.error("Force token refresh failed:", error);
            throw error;
        }
    }

    // Ensure Keycloak is initialized before any operation
    private async ensureInitialized(): Promise<void> {
        if (!this.initialized) {
            const result = await this.init();
            if (!result) {
                throw new Error("Keycloak initialization failed");
            }
        }
        if (!this.keycloak) {
            throw new Error("Keycloak initialization failed");
        }
    }

    public getToken(): string | undefined {
        return this.keycloak?.token;
    }

    public getRefreshToken(): string | undefined {
        return this.keycloak?.refreshToken;
    }

    public isAuthenticated(): boolean {
        return this.keycloak?.authenticated ?? false;
    }

    // Get a valid token, refreshing if necessary
    public async getValidToken(): Promise<string | undefined> {
        if (!this.keycloak?.authenticated) {
            return undefined;
        }

        try {
            // Refresh if token expires in less than 5 seconds
            // Using a smaller buffer to avoid refreshing too eagerly
            // which can cause issues with backend servers that have clock skew
            await this.keycloak?.updateToken(5);

            return this.keycloak?.token;
        } catch (error) {
            console.error("Failed to get valid token:", error);
            return undefined;
        }
    }

    public async getUserProfile() {
        try {
            await this.ensureInitialized();
            if (!this.keycloak?.authenticated) {
                return undefined;
            }
            return await this.keycloak?.loadUserProfile();
        } catch (error) {
            console.error("Failed to get user profile:", error);
            return undefined;
        }
    }
}

export default KeycloakService.getInstance();
